import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ArticleCard from "@/components/ArticleCard";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setArticle(null); setError(false);
    api.get(`/articles/${slug}`).then((r) => {
      setArticle(r.data);
      const base = r.data.meta_title || r.data.title;
      // Avoid duplicating the brand suffix when meta_title already contains it.
      const title = /grüne geldanlage/i.test(base) ? base : `${base} – Grüne Geldanlage`;
      document.title = title;
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", r.data.meta_description || r.data.excerpt);
      // Per-route canonical
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", window.location.origin + `/artikel/${r.data.slug}`);
    }).catch(() => setError(true));
    api.get("/articles").then((r) => setOthers(r.data)).catch(() => {});
  }, [slug]);

  if (error) return (
    <div className="min-h-screen bg-brand-paper"><Header />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl mb-4">Artikel nicht gefunden</h1>
        <Link to="/blog" className="text-brand-primary">Zur Blog-Übersicht</Link>
      </div><Footer /></div>
  );

  if (!article) return (
    <div className="min-h-screen bg-brand-paper"><Header />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-500">Artikel wird geladen…</div>
    <Footer /></div>
  );

  // Score-based related article selection:
  //   +5 pts for same category
  //   +2 pts per shared tag
  //   +1 pt tie-breaker for recency (newer first)
  // Show top 3.
  const RELATED_COUNT = 3;
  const currentTags = new Set(article.tags || []);
  const scored = others
    .filter((o) => o.slug !== slug)
    .map((o) => {
      let score = 0;
      if (o.category && o.category === article.category) score += 5;
      const shared = (o.tags || []).filter((t) => currentTags.has(t)).length;
      score += shared * 2;
      return { ...o, _score: score };
    })
    .sort((a, b) => {
      if (b._score !== a._score) return b._score - a._score;
      return new Date(b.published_at) - new Date(a.published_at);
    });
  const related = scored.slice(0, RELATED_COUNT);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.cover_image,
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { "@type": "Organization", name: article.author },
    publisher: { "@type": "Organization", name: "Grüne Geldanlage" },
  };

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="article-detail">
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <article className="max-w-4xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-brand-primary mb-8">
          <ArrowLeft size={14} /> Zurück zur Übersicht
        </Link>
        <div className="overline mb-4">{article.category}</div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight text-brand-dark mb-6">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mb-10">
          <span>{formatDate(article.published_at)}</span>
          <span>·</span>
          <span>{article.reading_time} Min. Lesezeit</span>
          <span>·</span>
          <span>{article.author}</span>
        </div>
        <div className="aspect-[16/9] overflow-hidden bg-neutral-100 mb-12">
          <img
            src={article.cover_image?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${article.cover_image}` : article.cover_image}
            alt={article.cover_alt || article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="prose-editorial max-w-none" dangerouslySetInnerHTML={{ __html: article.content }} />
      </article>

      {related.length > 0 && (
        <section className="bg-white border-t border-neutral-200" data-testid="related-widget">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24">
            <div className="flex items-end justify-between mb-10 md:mb-12">
              <div>
                <div className="overline mb-3">Verwandte Artikel</div>
                <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight">Das könnte Sie auch interessieren</h2>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-1 text-sm text-brand-primary font-medium">
                Alle Artikel →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-10 md:gap-8">
              {related.map((a) => (<ArticleCard key={a.id} article={a} />))}
            </div>
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
}
